import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import ScatterText from "./animation";
import profile from "../../../assets/profile.png";
// import cv from "../../../assets/CV.pdf";
import useResponsiveFont from "./useResponsiveFont";

interface Props {
  theme: number;
}

const bgColor: Record<number, string> = {
  1: "#87CEEB",
  2: "#FAFDD6",
  3: "#2F1252",
};

export const Landing: React.FC<Props> = (props) => {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 }); // percent

  // Mouse move listener
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const xPercent = (e.clientX / window.innerWidth) * 100;
      const yPercent = (e.clientY / window.innerHeight) * 100;
      setMousePos({ x: xPercent, y: yPercent });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <LandingContainer mousePos={mousePos} theme={props.theme}>
      <Content>
        <Profile>
          <ProfileImg src={profile} alt="profile" />

          <ScatterBox>
            <ScatterText
              text={`Good ${
                props.theme === 1
                  ? "morning"
                  : props.theme === 2
                  ? "afternoon"
                  : "evening"
              }, My name is`}
              fontSize={useResponsiveFont(2)}
            />
            <ScatterText
              text={`Watcharapol Treesatthayasakul (Ohm).`}
              fontSize={useResponsiveFont(2.5)}
            />
            <Break />
            <ScatterText
              text={`I'm 22 years old.`}
              fontSize={useResponsiveFont(2)}
            />
            <Break />
            <ScatterText
              text={`I'm a Master's student in Applied Mathematics at King Mongkut's University of Technology Thonburi, with a Bachelor's degree in Applied Computer Science.`}
              fontSize={useResponsiveFont(2)}
            />
            {/* <ScatterText
              text={`King Mongkut's University of Technology Thonburi,`}
              fontSize={useResponsiveFont(2)}
            />
            <ScatterText
              text={`with a Bachelor's degree in Applied Computer Science.`}
              fontSize={useResponsiveFont(2)}
            /> */}
            <Break />
            <ScatterText
              text={` I enjoy programming, learning new technologies, and solving problems.`}
              fontSize={useResponsiveFont(2)}
            />
            {/* <ScatterText
              text={`and solving problems.`}
              fontSize={useResponsiveFont(2)}
            /> */}
            <Break />
            <CVBox theme={props.theme}>
              <a href="/Curriculum Vitae.pdf" download>
                <span>📄 Download CV</span>
              </a>
            </CVBox>
          </ScatterBox>
        </Profile>
      </Content>
      <ScrollDown></ScrollDown>
    </LandingContainer>
  );
};

interface LandingContainerProps {
  mousePos: { x: number; y: number };
  theme: number;
}

const LandingContainer = styled("div")<LandingContainerProps>(
  ({ mousePos, theme }) => {
    const themeColors: Record<number, string[]> = {
      1: [
        "rgba(255, 223, 186, 0.4)", // sunrise orange
        "rgba(255, 255, 200, 0.4)", // soft yellow
        "rgba(255, 255, 255, 0.2)", // white glow
        "rgba(135, 206, 235, 0.3)", // sky blue
      ],
      2: [
        "rgba(235, 234, 226, 0.4)", // light golden
        "rgba(120, 223, 142, 0.3)", // bright yellow
        "rgba(150, 214, 233, 0.72)", // white highlight
        "rgba(224, 138, 224, 0.4)", // warm orange
      ],
      3: [
        "rgba(77, 8, 77, 0.5)", // deep purple
        "rgba(18, 87, 87, 0.5)", // teal
        "rgba(255, 255, 255, 0.1)", // faint white
        "rgba(59, 15, 52, 0.5)", // dark magenta
      ],
    };

    const colors = themeColors[theme] || themeColors[1];

    const lights = [
      `radial-gradient(circle at ${mousePos.x * 0.4 + 5}% ${
        mousePos.y * 0.3 + 5
      }%, ${colors[0]}, transparent 30%)`,
      `radial-gradient(circle at ${100 - mousePos.x}% ${100 - mousePos.y}%, ${
        colors[1]
      }, transparent 25%)`,
      `radial-gradient(circle at ${mousePos.x * 2 - 100}% ${
        150 - mousePos.y * 1.5
      }%, ${colors[2]}, transparent 20%)`,
      `radial-gradient(circle at ${mousePos.x * 0.5 + 10}% ${
        mousePos.y * 0.5 + 5
      }%, ${colors[3]}, transparent 20%)`,
    ];

    return {
      // position: "absolute",
      height: "100vh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "2rem",
      fontFamily: "'VT323', monospace",
      textAlign: "center",
      background: `${lights.join(", ")}, ${bgColor[theme]}`,
      maskImage:
        "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)",
      WebkitMaskImage:
        "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)",
      "@media (max-width: 1024px)": {
        height: "120vh",
      },
    };
  }
);

const Content = styled("div")({
  // maxWidth: "800px",
  lineHeight: 1.5,
  // border: "2px solid",
});

const CVBox = styled("div")<Props>(({ theme }) => ({
  fontSize: "1rem",
  "& a": {
    color: theme === 1 ? "#000" : theme === 2 ? "#000" : "#fff",
    textDecoration: "none",
    fontWeight: 500,
    position: "relative",
    display: "inline-block",
    padding: "2px 4px",
    transition: "color 0.3s ease",

    "&::before": {
      content: '"◄"',
      position: "absolute",
      right: "-1.2em", // place it just left of the text
      top: "50%",
      transform: "translateY(-50%)", // vertically center
      color: theme === 3 ? "#fff7" : "#0002", // semi-transparent arrow
      opacity: 0,
      transition: "opacity 0.3s ease",
    },

    "&:hover::before": {
      opacity: 1,
      animation: "floatArrow 2s ease-in-out infinite",
    },

    "&:hover": {
      color: theme === 3 ? "#fff7" : "#0002",
    },

    "@keyframes floatArrow": {
      "0%": { transform: "translateY(-50%) translateX(0)" },
      "50%": { transform: "translateY(-50%) translateX(5px)" },
      "100%": { transform: "translateY(-50%) translateX(0)" },
    },
  },
}));

const Profile = styled("div")({
  width: "70vw",
  display: "flex",
  justifyContent: "center",
  justifyItems: "center",
  gap: "5vw",
  "@media (max-width: 1440px)": {
    display: "block",
  },
});
const ScatterBox = styled("div")({
  position: "relative",
  width: "100%",
  justifyContent: "center",
  justifyItems: "center",
  left: 0,
});

const ProfileImg = styled("img")({
  width: "425px",
  height: "600px",
  objectFit: "cover",
  margin: "auto",

  "@media (max-width: 1440px)": {
    width: "250px",
    height: "350px",
  },
});
const Break = styled("br")({
  "@media (max-width: 1440px)": {
    display: "none",
  },
});
const ScrollDown = styled("div")({});
